import { Inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { ConcurrentUsageService } from '../concurrent-usage/concurrent-usage.service';
import { LEGACY_CONCURRENT_USAGE_KEYS } from '../concurrent-usage/concurrent-usage-keys';
import { JsonMaterialModel, MaterialModel } from '../../models/low-level/material.model';
import {
    JsonLocalStorageNotificationMaterialOrderModel,
    LocalStorageNotificationMaterialOrderModel,
    LocalStorageNotificationMaterialOrderModelFromJson,
} from '../../models/low-level/local-storage-notification-material-order.model';
import { catchError, map } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

const MATERIAL_KEY = LEGACY_CONCURRENT_USAGE_KEYS.material;
const MATERIAL_ORDER_KEY = LEGACY_CONCURRENT_USAGE_KEYS.materialOrder;

@Injectable({ providedIn: 'root' })
export class StorageMaterialService {
    public constructor(
        private readonly concurrentService: ConcurrentUsageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public getAllMaterialCreateOrders(): Observable<{
        material: LocalStorageNotificationMaterialOrderModel[];
        timestamp: number;
    }> {
        return this.concurrentService.readDataForKey(MATERIAL_ORDER_KEY).pipe(
            map((data) => {
                if (data?.length > 0) {
                    const rawJsonList = JSON.parse(data[0].value) as JsonLocalStorageNotificationMaterialOrderModel[];
                    return {
                        material: rawJsonList.map(
                            (rawEntry) => new LocalStorageNotificationMaterialOrderModelFromJson(rawEntry),
                        ),
                        timestamp: data[0].timestamp,
                    };
                }
                throw new Error(
                    '[StorageMaterialService] getAllMaterialCreateOrders: no order materials found in store.',
                );
            }),
            catchError((error) => {
                this.log.warn(
                    `[StorageMaterialService] getAllMaterialCreateOrders: Couldn't load value. Error: ${error}`,
                );
                return of({ material: [], timestamp: 0 });
            }),
        );
    }

    public saveMaterialCreateOrders(
        notificationMaterialOrders: LocalStorageNotificationMaterialOrderModel[],
        timestamp: number,
    ): Observable<void> {
        const outData = JSON.stringify(notificationMaterialOrders);
        this.log.debug(`${this.constructor.name}: saveMaterials`, {
            key: MATERIAL_ORDER_KEY,
            numMaterials: notificationMaterialOrders.length,
            timestamp,
        });
        return this.concurrentService.writeDataForKey(MATERIAL_ORDER_KEY, outData, timestamp).pipe(
            map(() => undefined),
            catchError((error) => {
                const message = `[StorageMaterialService] saveMaterialCreateOrders: Couldn't store material value. Error: ${error}`;
                this.log.error(message);
                return throwError(() => new Error(message));
            }),
        );
    }

    public saveMaterials(materials: MaterialModel[], timestamp: number): Observable<void> {
        const outData = JSON.stringify(materials);
        this.log.debug(`${this.constructor.name}: saveMaterials`, {
            key: MATERIAL_KEY,
            numMaterials: materials.length,
            timestamp,
        });
        return this.concurrentService.writeDataForKey(MATERIAL_KEY, outData, timestamp).pipe(
            map(() => undefined),
            catchError((error) => {
                const message = `[StorageMaterialService] saveMaterial: Couldn't store material value. Error: ${error}`;
                this.log.error(message);
                return throwError(() => new Error(message));
            }),
        );
    }

    public getMaterials(): Observable<{ material: JsonMaterialModel[]; timestamp: number }> {
        return this.concurrentService.readDataForKey(MATERIAL_KEY).pipe(
            map((data) => {
                if (data?.length > 0) {
                    return {
                        material: JSON.parse(data[0].value) as JsonMaterialModel[],
                        timestamp: data[0].timestamp,
                    };
                }
                throw new Error('[StorageMaterialService] getMaterials: no materials found in store.');
            }),
            catchError((error) => {
                this.log.warn(`[StorageMaterialService] getMaterials: Couldn't load value. Error: ${error}`);
                return of({ material: [], timestamp: 0 });
            }),
        );
    }
}
